(() => {
  const CONFIG_SELECTOR = '[data-heart-scroll]';
  const MOBILE_QUERY = window.matchMedia('(max-width: 749px)');
  const REDUCED_MOTION_QUERY = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const clamp = (value, min = 0, max = 1) =>
    Math.min(max, Math.max(min, value));

  const lerp = (start, end, progress) =>
    start + (end - start) * progress;

  const easeInOutCubic = (value) => {
    const t = clamp(value);

    return t < 0.5
      ? 4 * t * t * t
      : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  const copyImageAttributes = (source, destination) => {
    ['src', 'srcset', 'sizes', 'width', 'height'].forEach(
      (attribute) => {
        const value = source.getAttribute(attribute);

        if (value) {
          destination.setAttribute(attribute, value);
        }
      }
    );

    destination.alt = '';
    destination.loading = 'eager';
    destination.decoding = 'async';
    destination.draggable = false;
  };

  const findFollowingTarget = (config, selector) => {
    const wrapper =
      config.closest('.shopify-section') ||
      config.parentElement;

    let sibling = wrapper?.nextElementSibling || null;

    while (sibling) {
      if (sibling.matches?.(selector)) {
        return sibling;
      }

      const nestedMatch = sibling.querySelector?.(selector);

      if (nestedMatch) {
        return nestedMatch;
      }

      /*
        Product-grid fallback for templates where the Shopify wrapper
        class was changed but the section root ID remains intact.
      */
      if (
        sibling.matches?.('[id^="SlayProductGrid-"]') ||
        sibling.querySelector?.('[id^="SlayProductGrid-"]')
      ) {
        return (
          sibling.matches?.('[id^="SlayProductGrid-"]')
            ? sibling
            : sibling.querySelector('[id^="SlayProductGrid-"]')
        );
      }

      sibling = sibling.nextElementSibling;
    }

    /*
      Final fallback: choose the first matching target that occurs after
      this configuration section in document order.
    */
    const candidates = Array.from(
      document.querySelectorAll(selector)
    );

    return (
      candidates.find((candidate) => {
        const relation =
          config.compareDocumentPosition(candidate);

        return Boolean(
          relation & Node.DOCUMENT_POSITION_FOLLOWING
        );
      }) ||
      document.querySelector('[id^="SlayProductGrid-"]') ||
      null
    );
  };

  const createPortal = (config) => {
    const owner = config.dataset.heartOwner;

    document
      .querySelectorAll(
        `.sm-heart-scroll-portal[data-heart-owner="${owner}"]`
      )
      .forEach((oldPortal) => oldPortal.remove());

    const leftSource = config.querySelector(
      '[data-heart-source-left]'
    );

    const rightSource = config.querySelector(
      '[data-heart-source-right]'
    );

    if (!leftSource || !rightSource) {
      return null;
    }

    const portal = document.createElement('div');
    portal.className = 'sm-heart-scroll-portal';
    portal.dataset.heartOwner = owner;
    portal.setAttribute('aria-hidden', 'true');

    const debugLabel = document.createElement('div');
    debugLabel.className = 'sm-heart-scroll-portal__debug';
    debugLabel.textContent = 'Heart target: waiting';

    const leftHand = document.createElement('div');
    leftHand.className =
      'sm-heart-scroll-portal__hand ' +
      'sm-heart-scroll-portal__hand--left';

    const rightHand = document.createElement('div');
    rightHand.className =
      'sm-heart-scroll-portal__hand ' +
      'sm-heart-scroll-portal__hand--right';

    const leftImage = document.createElement('img');
    leftImage.className = 'sm-heart-scroll-portal__image';
    copyImageAttributes(leftSource, leftImage);

    const rightImage = document.createElement('img');
    rightImage.className = 'sm-heart-scroll-portal__image';
    copyImageAttributes(rightSource, rightImage);

    leftHand.appendChild(leftImage);
    rightHand.appendChild(rightImage);

    portal.appendChild(debugLabel);
    portal.appendChild(leftHand);
    portal.appendChild(rightHand);

    document.body.appendChild(portal);

    return {
      portal,
      debugLabel,
      leftHand,
      rightHand
    };
  };

  const initHeartScroll = (config) => {
    if (!config || config.dataset.heartReady === 'true') {
      return;
    }

    const portalParts = createPortal(config);

    if (!portalParts) {
      console.warn(
        '[Slay heart scroll] Hand image sources were not found.'
      );
      return;
    }

    config.dataset.heartReady = 'true';

    const {
      portal,
      debugLabel,
      leftHand,
      rightHand
    } = portalParts;

    const targetSelector =
      config.dataset.targetSelector ||
      '.section-slay-product-grid';

    let target = null;
    let frameRequested = false;

    const syncSettings = () => {
      const computed = getComputedStyle(config);

      [
        '--sm-heart-sticky-top',
        '--sm-heart-hand-width',
        '--sm-heart-hand-width-mobile',
        '--sm-heart-vertical',
        '--sm-heart-z-index'
      ].forEach((property) => {
        const value = computed.getPropertyValue(property);

        if (value) {
          portal.style.setProperty(property, value);
        }
      });

      portal.classList.toggle(
        'is-debug',
        config.dataset.debug === 'true'
      );
    };

    const resolveTarget = () => {
      target = findFollowingTarget(config, targetSelector);

      if (!target) {
        debugLabel.textContent =
          `Target not found: ${targetSelector}`;

        console.warn(
          '[Slay heart scroll] Product grid not found.',
          {
            selector: targetSelector,
            config
          }
        );

        portal.classList.remove('is-active');
        return false;
      }

      debugLabel.textContent =
        `Target found: ${targetSelector}`;

      return true;
    };

    const render = () => {
      frameRequested = false;
      syncSettings();

      if (!target?.isConnected && !resolveTarget()) {
        return;
      }

      const viewportWidth = Math.max(window.innerWidth, 1);
      const viewportHeight = Math.max(window.innerHeight, 1);
      const targetRect = target.getBoundingClientRect();

      const stickyTop =
        parseFloat(
          getComputedStyle(config).getPropertyValue(
            '--sm-heart-sticky-top'
          )
        ) || 0;

      const triggerOffset =
        Number(config.dataset.triggerOffset || 0) / 100;

      const triggerLine =
        stickyTop + viewportHeight * triggerOffset;

      const rangeVh =
        Number(config.dataset.scrollRange || 140);

      const animationDistance = Math.max(
        viewportHeight * (rangeVh / 100),
        1
      );

      /*
        This calculation uses the product grid's viewport position,
        not window.scrollY. It therefore works in standard pages,
        Shopify's desktop preview iframe, and themes with a custom
        scrolling container.
      */
      const rawProgress =
        (triggerLine - targetRect.top) /
        animationDistance;

      const active =
        rawProgress >= -0.015 &&
        rawProgress <= 1.015 &&
        targetRect.bottom > stickyTop;

      portal.classList.toggle('is-active', active);

      const respectReducedMotion =
        config.dataset.respectReducedMotion === 'true';

      if (
        respectReducedMotion &&
        REDUCED_MOTION_QUERY.matches
      ) {
        portal.classList.remove('is-active');
        return;
      }

      let progress = clamp(rawProgress);

      if (config.dataset.direction === 'close') {
        progress = 1 - progress;
      }

      const eased = easeInOutCubic(progress);
      const isMobile = MOBILE_QUERY.matches;

      const travelVw = Number(
        isMobile
          ? config.dataset.mobileOpenTravel || 90
          : config.dataset.openTravel || 70
      );

      const travelPx =
        viewportWidth * (travelVw / 100);

      const closedInset = Number(
        config.dataset.closedInset || 0
      );

      const leftX = lerp(
        closedInset,
        -travelPx,
        eased
      );

      const rightX = lerp(
        -closedInset,
        travelPx,
        eased
      );

      let opacity = 1;

      if (config.dataset.fadeEnd === 'true') {
        const fadeStart =
          Number(config.dataset.fadeStart || 85) / 100;

        opacity =
          progress <= fadeStart
            ? 1
            : 1 -
              clamp(
                (progress - fadeStart) /
                  Math.max(1 - fadeStart, 0.001)
              );
      }

      leftHand.style.setProperty(
        '--sm-heart-x',
        `${leftX.toFixed(2)}px`
      );

      rightHand.style.setProperty(
        '--sm-heart-x',
        `${rightX.toFixed(2)}px`
      );

      leftHand.style.setProperty(
        '--sm-heart-opacity',
        opacity.toFixed(3)
      );

      rightHand.style.setProperty(
        '--sm-heart-opacity',
        opacity.toFixed(3)
      );

      if (config.dataset.debug === 'true') {
        debugLabel.textContent =
          `Target found · progress ${Math.round(
            clamp(rawProgress) * 100
          )}%`;
      }
    };

    const requestRender = () => {
      if (frameRequested) return;

      frameRequested = true;
      window.requestAnimationFrame(render);
    };

    /*
      Capture scroll events from window, document, and custom scrolling
      containers used by some desktop themes.
    */
    window.addEventListener('scroll', requestRender, {
      passive: true
    });

    document.addEventListener(
      'scroll',
      requestRender,
      {
        passive: true,
        capture: true
      }
    );

    window.addEventListener('resize', requestRender);

    MOBILE_QUERY.addEventListener?.(
      'change',
      requestRender
    );

    REDUCED_MOTION_QUERY.addEventListener?.(
      'change',
      requestRender
    );

    const mutationObserver = new MutationObserver(
      requestRender
    );

    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    resolveTarget();
    render();

    config._smHeartCleanup = () => {
      mutationObserver.disconnect();
      portal.remove();
    };
  };

  const initAll = (root = document) => {
    root
      .querySelectorAll(CONFIG_SELECTOR)
      .forEach(initHeartScroll);
  };

  document.addEventListener(
    'DOMContentLoaded',
    () => initAll()
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => initAll(event.target)
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      const config = event.target.querySelector?.(
        CONFIG_SELECTOR
      );

      config?._smHeartCleanup?.();
    }
  );
})();
